import React, { useEffect, useMemo } from 'react';
import { useI18n } from 'next-localization';

import { format, parse } from 'date-fns';
import { pick } from 'lodash-es';
import { createPortal } from 'react-dom';

import { useModalContext } from '@/components/modals/modal-context';
import { PhysicianData, ProviderLocation } from '@/features/profile/profile.types';
import { useBasePath } from '@/hooks/use-base-path';
import { useCustomRouting } from '@/hooks/use-custom-routing';
import { useSessionStorageWithCollection } from '@/hooks/use-session-storage-collection';
import {
  BOOKING_CONFIRMATION_DATA,
  CELEBRUS_CONTINUE_BUTTON_ID_4,
  CONFIRMATION_PAGE,
} from '@/lib/constants';
import { physicianDisplayTitle } from '@/lib/utils';
import { NeuFeedback } from '@hcahealthcaredpx/neutron-react';

import { AppointmentDetails, ConfirmationData } from '../../booking.types';
import { useBookingContext } from '../../booking-context';
import { useBookAppointmentMutation } from '../../hooks';
import { NavigationButtons } from '../navigation-buttons';
import { HOLD_API_DATE_TIME_FORMAT, TIME_NOT_BOOKED_ERROR_MODAL } from '../step-one/constants';
import AppointmentInformationSection from './appointment-information-section';
import PatientInformationSection from './patient-information-section';
import styles from './step-three.module.css';
import { buildPayload } from './utils';

interface StepThreeProps {
  goToPreviousStep: () => void;
  physicianData: PhysicianData;
  physicianName: string;
  siteType: string;
  location: ProviderLocation;
  beforeUnloadHandler: (event: BeforeUnloadEvent) => void;
}

// todo: add a simple fn in the hook to check for old bookings and remove them
// run on useEffect of mounting confirmation page

// todo: extract
export const formatAppointment = (dateTimeString?: string): string => {
  if (!dateTimeString) return 'no date is booked';

  const parsed = parse(dateTimeString, HOLD_API_DATE_TIME_FORMAT, new Date());
  // Format as 'Thursday, Aug 21 at 8:45am'
  return format(parsed, "EEEE, MMM d 'at' h:mm a");
};

export const StepThree = ({
  goToPreviousStep,
  physicianData,
  physicianName,
  siteType,
  location,
  beforeUnloadHandler,
}: StepThreeProps): JSX.Element => {
  const { t } = useI18n();
  const { setToStorage: setWithExpiry } = useSessionStorageWithCollection();
  const { stepOneData, stepTwoData, setConfirmationData } = useBookingContext();

  const [loadingToConfirmation, setLoadingToConfirmation] = React.useState(false);

  const { routerPush } = useCustomRouting();
  const { withBasePath } = useBasePath();
  const { mutate, isPending, isSuccess, isError, data } = useBookAppointmentMutation();

  const physicianNpi = pick(physicianData, ['physicianNpi']);

  const providerSpecialty = useMemo(() => {
    return (
      physicianData?.providerSpecialties.find((specialty) => specialty.primary)?.specialty || ''
    );
  }, [physicianData]);

  useEffect(() => {
    if (isSuccess || !isPending) {
      const { dateTimeOfAppointment, location, appointmentTypeLabel, reasonForVisit } = stepOneData;

      const physData = pick(physicianData, [
        'physicianPhotoUrl',
        'physicianFirstName',
        'physicianMiddleInitial',
        'physicianLastName',
        'physicianDesignation',
        'urlTitle',
      ]);

      const locationData = {
        name: location?.name || '',
        street: location?.street || '',
        city: location?.city || '',
        state: location?.state || '',
        zip: location?.zip || '',
        phone: location?.phone || '',
      };

      // Type guard to check if data?.data is AppointmentDetails
      function isAppointmentDetails(obj: unknown): obj is AppointmentDetails {
        return (
          !!obj &&
          typeof obj === 'object' &&
          'appointmentTypeName' in obj &&
          'bookingId' in obj &&
          'createdDateTime' in obj &&
          'id' in obj
        );
      }

      const responseData = data?.data;

      if (!isAppointmentDetails(responseData)) {
        return;
      }

      const confirmationData: ConfirmationData = {
        dateTimeOfAppointment: dateTimeOfAppointment || '',
        location: locationData,
        physician: {
          ...physData,
          providerSpecialty,
        },
        appointmentTypeLabel: appointmentTypeLabel || '',
        reasonForVisit,
        _response: responseData,
      };

      try {
        const holdId = stepOneData.holdId;
        if (holdId) {
          // Todo: save confirmation data to context
          // setConfirmationData(confirmationData);
          setWithExpiry({
            key: BOOKING_CONFIRMATION_DATA,
            itemKey: `${holdId}`,
            data: confirmationData,
          });
          sessionStorage.setItem(holdId, physData.urlTitle);
        }
      } catch (error) {
        console.error('error saving confirmation data to LS', error);
      }
      const url = `${CONFIRMATION_PAGE}/${stepOneData.holdId}`;
      routerPush(withBasePath(url));
    }
  }, [
    isSuccess,
    isPending,
    routerPush,
    stepOneData,
    physicianData,
    setWithExpiry,
    providerSpecialty,
    data,
    setConfirmationData,
    withBasePath,
  ]);

  const { openModal } = useModalContext();

  useEffect(() => {
    if (isError) {
      console.error('Error booking appointment', data);
      setLoadingToConfirmation(false);
      openModal(TIME_NOT_BOOKED_ERROR_MODAL);
    }
  }, [isError, data, openModal]);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    window.removeEventListener('beforeunload', beforeUnloadHandler);

    if (!stepOneData.holdId) {
      console.error('No holdId, cannot make request.', stepOneData);
      return;
    }

    const additionalData = {
      siteType,
    };

    // Todo: if no data do not try to submit - show alert or no timeslot available modal
    const payload = buildPayload({
      ...stepOneData,
      ...stepTwoData,
      ...physicianNpi,
      ...additionalData,
    });
    mutate(payload);
    setLoadingToConfirmation(true);
    // Todo: we could trigger a timeout here to catch if the request takes too long
    // then remove the loading state, and maybe show some error.
  };

  const formatBirthDate = () => {
    const dob = stepTwoData.dateOfBirth;
    if (!dob) return '';
    // Use date-fns to parse and format as MM/DD/YYYY
    try {
      const parsed = parse(dob, 'yyyy-MM-dd', new Date());
      return format(parsed, 'MM/dd/yyyy');
    } catch {
      return dob;
    }
  };

  const fieldsData = {
    appointmentInformation: {
      physicianName: physicianDisplayTitle(physicianName, physicianData.physicianDesignation),
      physicianPhotoUrl: physicianData.physicianPhotoUrl,
      appointmentTime: formatAppointment(stepOneData.dateTimeOfAppointment),
      hospitalName: location.name,
      address: location.street,
      city: `${location.city}, ${location.state} ${location.zip}`,
      appointmentType: stepOneData.appointmentTypeLabel || '',
      reasonForVisit: stepOneData.reasonForVisit,
    },
    patientInformation: {
      patientName: `${stepTwoData.firstName} ${stepTwoData.lastName}`,
      birthDate: formatBirthDate(),
      gender: stepTwoData.gender,
      address: stepTwoData.address1,
      address2: stepTwoData.address2,
      city: stepTwoData.city,
      state: stepTwoData.state,
      zip: stepTwoData.zip,
      phone: stepTwoData.phoneNumber,
      email: stepTwoData.email,
    },
  };

  return (
    <>
      <div className="row">
        <div className="col-12">
          <div className="row mt-5 mb-2">
            <div className="col-12 pt-2 pb-2">
              <h1 className="neu-display-2 neu-text-gray-70">
                {t('OAS.Booking.Confirmation.ReviewTitle')}
              </h1>
            </div>
            <div className={styles.divider}></div>
          </div>
          <AppointmentInformationSection fields={fieldsData} />
          <PatientInformationSection fields={fieldsData} />
        </div>
      </div>
      <form onSubmit={onSubmit}>
        <NavigationButtons
          goToPreviousStep={goToPreviousStep}
          isLastStep
          isContinueDisabled={isPending}
          continueButtonId={CELEBRUS_CONTINUE_BUTTON_ID_4}
        />
      </form>
      {createPortal(
        <>
          {(loadingToConfirmation || isPending) && (
            <div className={styles.loadingOverlay}>
              <NeuFeedback
                color="blue"
                kind="spinner"
                label={t('OAS.Booking.Confirmation.Loading') || 'Loading...'}
              />
            </div>
          )}
        </>,
        document.body
      )}
    </>
  );
};
